import { GetCountriesUseCase } from '@/core/domain/countries/usecases/get-countries.usecase';
import { PurchasePersistenceModel } from '@/core/domain/purchase-persistence/models/PurchasePersistence.model';
import { GetPurchaseUseCase } from '@/core/domain/purchase-persistence/usecases/get-purchase-persistence.usecase';
import { GetStatesUseCase } from '@/core/domain/states/usecases/get-states.usecase';
import { TranslationsGetLanguageUseCase } from '@/core/domain/translation/usecases/translations-get-language.usecase';
import {
    Component,
    DestroyRef,
    EventEmitter,
    inject,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
    catchError,
    combineLatest,
    filter,
    map,
    Observable,
    of,
    startWith,
    switchMap,
} from 'rxjs';
import { ValidationService } from '../../../../common/validation/validation.service';
import { CardSchema } from '../../../../core/domain/recurly/models/CardSchema';
import { ANIMATIONS_CARD } from '../card-amount/card-amount.component';

interface SelectOption {
    id: string;
    name: string;
}

@Component({
    selector: 'vg-card-payment',
    templateUrl: './vg-card-payment.component.html',
    styleUrls: ['./vg-card-payment.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: ANIMATIONS_CARD,
})
export class VgCardPaymentComponent {
    public paymentForm: FormGroup;
    public paymentInformation = {
        cardNumber: '',
    };
    public cardSchema: CardSchema = {
        bin: '',
        scheme: '',
    };
    public purchasePersistence: PurchasePersistenceModel | null = null;
    public state = null;
    public country = null;
    public isStateDisabled = true;

    private readonly destroyRef = inject(DestroyRef);

    public countries$: Observable<SelectOption[]> = this.getLanguage
        .execute()
        .pipe(
            switchMap(({ language }) =>
                this.getCountries.execute({
                    language: this.formatLanguage(language),
                }),
            ),
            map((data) => this.toSelectOptions(data.countries)),
            startWith([]),
            catchError(() => of([])),
        );

    public states$: Observable<SelectOption[]>;

    @Output() changes = new EventEmitter();
    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly validationService: ValidationService,
        private readonly getPurchasePersistence: GetPurchaseUseCase,
        private readonly getCountries: GetCountriesUseCase,
        private readonly getStates: GetStatesUseCase,
        private readonly getLanguage: TranslationsGetLanguageUseCase,
    ) {
        this.paymentForm = this.formBuilder.group({
            cardNumber: ['', Validators.compose([Validators.required])],
            expirationDate: [
                '',
                Validators.compose([
                    Validators.required,
                    Validators.minLength(4),
                ]),
            ],
            cvv: [
                '',
                Validators.compose([
                    Validators.required,
                    Validators.minLength(3),
                ]),
            ],
            firstName: [
                '',
                Validators.compose([
                    Validators.required,
                    this.validationService.withoutSpecialCharNumber,
                ]),
            ],
            lastName: [
                '',
                Validators.compose([
                    Validators.required,
                    this.validationService.withoutSpecialCharNumber,
                ]),
            ],
            address: ['', Validators.compose([Validators.required])],
            city: ['', Validators.compose([Validators.required])],
            state: ['', Validators.compose([Validators.required])],
            postalCode: [
                '',
                Validators.compose([
                    Validators.required,
                    Validators.minLength(5),
                ]),
            ],
            country: ['', Validators.compose([Validators.required])],
        });

        this.paymentForm.statusChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() =>
                this.changes.emit({
                    valid: this.paymentForm.valid,
                    data: this.paymentForm.value,
                }),
            );

        const countryChanges$ = this.paymentForm
            .get('country')!
            .valueChanges.pipe(takeUntilDestroyed(this.destroyRef));

        this.states$ = combineLatest([
            countryChanges$,
            this.getLanguage.execute(),
        ]).pipe(
            filter(([countryCode]) => !!countryCode),
            switchMap(([countryCode, { language }]) =>
                this.getStates
                    .execute({
                        countryCode,
                        language: this.formatLanguage(language),
                    })
                    .pipe(
                        map((data) => this.toSelectOptions(data.provinces)),
                        catchError(() => of([])),
                    ),
            ),
            startWith([]),
        );

        countryChanges$.subscribe((countryValue) => {
            this.isStateDisabled = !countryValue;
            this.paymentForm.get('state')?.reset();
        });
    }

    public initPurchasePersistence(): void {
        this.getPurchasePersistence
            .execute()
            .subscribe((purchasePersistence) => {
                this.purchasePersistence = purchasePersistence;
            });
    }

    public onGetCardSchema(cardSchema: CardSchema) {
        this.cardSchema = cardSchema;
        if (
            this.cardSchema.scheme !== 'american-express' &&
            this.cardSchema.scheme.length > 0
        ) {
            this.paymentForm
                .get('cardNumber')
                ?.setValidators([
                    Validators.required,
                    Validators.minLength(16),
                ]);
            this.paymentForm.get('cardNumber')?.updateValueAndValidity();
        }
        if (this.cardSchema.scheme === 'american-express') {
            this.paymentForm
                .get('cardNumber')
                ?.setValidators([
                    Validators.required,
                    Validators.minLength(15),
                ]);
            this.paymentForm.get('cardNumber')?.updateValueAndValidity();
        }
    }

    private formatLanguage(language: string): string {
        const [lang, region] = language.split('-');
        return region ? `${lang}-${region.toUpperCase()}` : language;
    }

    private toSelectOptions(
        items: Array<{ code: string; name: string }>,
    ): SelectOption[] {
        return items.map((item) => ({
            id: item.code,
            name: item.name,
        }));
    }
}
